import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@store/store-helper";
import { selectedProjectSelector } from "@store/projects/projects-selector";

// Keep in sync with index.html:
export const DEFAULT_TITLE = "Sphere XG";/* The title to set. Use undefined if you don't have all data to build the title yet. */

/**
 * Define the routes and respective document title
 */
type TitleMapEntry = {
  /* The part of the route that is included. */
  routePart: string;
  /**
   * The title to set
   * Use undefined if you don't have all data to build the title yet.
   * This prevents flickering of the title while loading.
   */
  title?: string;
}

/**
 * Sets the document.title based on the selected project and the current pathname.
 * This should be the only place where the title is set.
 *
 * Dashboard:              Sphere XG
 * Dashboard Project sel.: Project name
 * Data management:        Project name - Editor
 * Registration:           Project name - Editor (not this codebase)
 * Viewer:                 Project name - Viewer (not this codebase)
 */
export function useDocumentTitle(): void {
  const location = useLocation();
  const selectedProject = useAppSelector(selectedProjectSelector);

  useEffect(() => {
    const projectName: string | undefined = selectedProject?.name;

    // Define the titles for different routes. First match wins.
    const titleMap: TitleMapEntry[] = [
      {
        // Example /{UUID}/projects/{UUID}/data-management
        routePart: "/data-management",
        // Prevent title flickering by returning undefined while data loads.
        // This keeps the previous title until we have all data to show the new one.
        title: projectName ? `${projectName} - Editor` : undefined,
      },
      {
        // Example /{UUID}/projects/active
        routePart: "/projects/active",
        title: DEFAULT_TITLE,
      },
      {
        // Example /{UUID}/projects/archived
        routePart: "/projects/archived",
        title: DEFAULT_TITLE,
      },
      {
        // Example /{UUID}/projects/{UUID}/details
        routePart: "/projects/",
        title: projectName,
      },
      {
        // Fallback to the default title
        routePart: "/",
        title: DEFAULT_TITLE,
      },
    ];

    let newTitle: string | undefined;
    for (const item of titleMap) {
      if (location.pathname.includes(item.routePart)) {
        newTitle = item.title;
        break;
      }
    }

    if (newTitle) {
      // The only place, where document.title should be set.
      document.title = newTitle;
    }
  }, [location.pathname, selectedProject]);
}
